'use client';
import { useProductTabSelection } from '@eliadmin/core/src/components/product/detail/tabs/use-product-tab-selection';
import {
  ContentFieldTabTriggers,
  ContentFieldTabContents,
} from '@eliadmin/core/src/components/content-fields/content-field-tabs';
import { ProductTabAccessories } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-accessories';
import { ProductTabAttachments } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-attachments';
import { ProductTabDescription } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-description';
import { ProductTabParameters } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-parameters';
import { ProductTabReviews } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-reviews';
import { ProductTabSuppliers } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-suppliers';
import { ProductTabVideo } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-video';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { PropsWithChildren } from 'react';

enum ProductTab {
  DESCRIPTION = 'description',
  SUPPLIERS = 'suppliers',
  PARAMETERS = 'parameters',
  REVIEWS = 'reviews',
  ACCESSORIES = 'accessories',
  VIDEO = 'video',
  ATTACHMENTS = 'attachments',
}

type ProductTabsProps = {
  className?: string;
};

function ProductTabs({ className, children }: PropsWithChildren<ProductTabsProps>) {
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const product = useVariantsStore(s => s.product);
  const selectedVariant = useVariantsStore(s => s.selectedVariant);
  const selection = useProductTabSelection(
    product,
    selectedVariant,
    templateSettings.product_detail,
    { excludeIds: [2] },
  );

  return (
    <Tabs
      value={selection.value}
      onValueChange={selection.onValueChange}
      className={cn('ea-product-detail-tabs', className)}
    >
      {children}
    </Tabs>
  );
}

type ProductTabsListProps = {
  className?: string;
};

function ProductTabsList(props: PropsWithChildren<ProductTabsListProps>) {
  return (
    <div className="w-full">
      <div className="hide-scroll-bar m-auto w-full max-w-screen-xl overflow-x-scroll px-4">
        <TabsList
          className={cn(
            'hide-scroll-bar box-border justify-start overflow-visible rounded-none bg-transparent p-0',
            'ea-product-detail-tabs__list',
            props.className,
          )}
        >
          {props.children}
        </TabsList>
      </div>
    </div>
  );
}

type ProductTabsTriggersProps = {
  className?: string;
};

function ProductTabsTriggers({ className }: ProductTabsTriggersProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const { t } = useTranslations();
  const product = useVariantsStore(s => s.product);

  className = cn(
    'mr-1 h-full rounded-none rounded-t-sm bg-transparent shadow-none text-xs transition-none',
    'text-[hsl(var(--primary))]',
    'data-[state=active]:text-black data-[state=active]:border-none data-[state=active]:shadow-none',
    className,
  );

  return (
    <>
      {!!templateSettings.product_detail?.description?.enabled && !!product.description && (
        <TabsTrigger
          value={ProductTab.DESCRIPTION}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__description-trigger',
            className,
          )}
        >
          {t('text231')}
        </TabsTrigger>
      )}
      {!!templateSettings.product_detail?.parameters?.enabled && !!product.attributes?.length && (
        <TabsTrigger
          value={ProductTab.PARAMETERS}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__paremeters-trigger',
            className,
          )}
        >
          {t('text9')}
        </TabsTrigger>
      )}
      {!!templateSettings.product_detail?.reviews?.enabled && (
        <TabsTrigger
          value={ProductTab.REVIEWS}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__reviews-trigger',
            className,
          )}
        >
          {t('text447')}
        </TabsTrigger>
      )}
      {!!templateSettings.product_detail?.video?.enabled && !!product.video && (
        <TabsTrigger
          value={ProductTab.VIDEO}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__video-trigger',
            className,
          )}
        >
          {t('text451')}
        </TabsTrigger>
      )}
      {!!templateSettings.product_detail?.accessories?.enabled && !!product.accessories?.length && (
        <TabsTrigger
          value={ProductTab.ACCESSORIES}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__accesories-trigger',
            className,
          )}
        >
          {t('text232')}
        </TabsTrigger>
      )}
      {/* {!!templateSettings.product_detail?.attachments?.enabled && !!product.attachments?.length && ( */}
      {!!product.attachments?.length && (
        <TabsTrigger
          value={ProductTab.ATTACHMENTS}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__accesories-trigger',
            className,
          )}
        >
          {t('text333')}
        </TabsTrigger>
      )}
      {!!templateSettings.product_detail?.suppliers?.enabled && (
        <TabsTrigger
          value={ProductTab.SUPPLIERS}
          className={cn(
            'text-left text-[14px]',
            'ea-product-detail-tabs__accesories-trigger',
            className,
          )}
        >
          {t('text718')}
        </TabsTrigger>
      )}
      <ContentFieldTabTriggers
        owner={product}
        module="products"
        excludeIds={[2]}
        className={className}
      />
    </>
  );
}

type ProductTabsContentProps = {
  className?: string;
};

function ProductTabsContent(props: ProductTabsContentProps) {
  const product = useVariantsStore(s => s.product);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const { t } = useTranslations();
  return (
    <div className="w-full">
      <div
        className={cn(
          'm-auto w-full max-w-screen-xl sm:px-4',
          'ea-product-detail-tabs__content',
          props.className,
        )}
      >
        {!!templateSettings.product_detail?.description?.enabled && !!product.description && (
          <TabsContent
            value={ProductTab.DESCRIPTION}
            className={cn('mr-0', 'ea-product-detail-tabs__description')}
          >
            <ProductTabDescription />
          </TabsContent>
        )}

        {!!templateSettings.product_detail?.parameters?.enabled && !!product.attributes?.length && (
          <TabsContent
            value={ProductTab.PARAMETERS}
            className={cn('mr-0', 'ea-product-detail-tabs__parameters')}
          >
            <ProductTabParameters />
          </TabsContent>
        )}

        {!!templateSettings.product_detail?.reviews?.enabled && (
          <TabsContent
            value={ProductTab.REVIEWS}
            className={cn('mr-0', 'ea-product-detail-tabs__reviews')}
          >
            <ProductTabReviews />
          </TabsContent>
        )}
        {!!templateSettings.product_detail?.video?.enabled && !!product.video && (
          <TabsContent
            value={ProductTab.VIDEO}
            className={cn('mr-0', 'ea-product-detail-tabs__video')}
          >
            <ProductTabVideo />
          </TabsContent>
        )}
        {!!templateSettings.product_detail?.accessories?.enabled &&
          !!product.accessories?.length && (
            <TabsContent
              value={ProductTab.ACCESSORIES}
              className={cn('mr-0', 'ea-product-detail-tabs__accesories')}
            >
              <ProductTabAccessories />
            </TabsContent>
          )}
        {/* {!!templateSettings.product_detail?.attachments?.enabled && !!product.attachments?.length && ( */}
        {!!product.attachments?.length && (
          <TabsContent
            value={ProductTab.ATTACHMENTS}
            className={cn('mr-0', 'ea-product-detail-tabs__attachments')}
          >
            <ProductTabAttachments />
          </TabsContent>
        )}
        {!!templateSettings.product_detail?.suppliers?.enabled && (
          <TabsContent
            value={ProductTab.SUPPLIERS}
            className={cn('mr-0', 'ea-product-detail-tabs__suppliers')}
          >
            <ProductTabSuppliers />
          </TabsContent>
        )}
        <ContentFieldTabContents
          owner={product}
          module="products"
          excludeIds={[2]}
          className={cn('mr-0', 'ea-product-detail-tabs__accesories')}
        />
      </div>
    </div>
  );
}

export { ProductTabs, ProductTabsContent, ProductTabsList, ProductTabsTriggers };
